import { Ionicons } from "@/ui/icons";
import AsyncStorage from "@/platform/async-storage";
import React from "react";
import { Pressable, StyleSheet, Text, View } from "@/ui/rn";
import { T } from "@/utils/i18n";

const PRIMARY = "#294956";
const FATAL_LOG_KEY = "@TirvuMais:last_fatal_error";

/**
 * Persiste o último erro fatal (best-effort) para diagnóstico posterior. Como o
 * app não tinha NENHUM ErrorBoundary nem handler global, qualquer exceção JS de
 * render (ou não capturada em callback) fechava o app SEM deixar rastro — por
 * isso os crashes "em alguns celulares" eram invisíveis. Aqui registramos a
 * assinatura (mensagem + stack + quando) para podermos enviar/inspecionar depois.
 */
async function persistFatal(kind: string, error: any) {
  try {
    const payload = {
      kind,
      message: String(error?.message ?? error ?? "erro desconhecido"),
      stack: String(error?.stack ?? "").slice(0, 4000),
      at: new Date().toISOString(),
    };
    await AsyncStorage.setItem(FATAL_LOG_KEY, JSON.stringify(payload));
  } catch {
    /* ignore */
  }
}

/** Lê (e opcionalmente limpa) o último erro fatal registrado. */
export async function readLastFatalError(clear = false): Promise<any | null> {
  try {
    const raw = await AsyncStorage.getItem(FATAL_LOG_KEY);
    if (clear) await AsyncStorage.removeItem(FATAL_LOG_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

/**
 * Handler global de exceções JS NÃO capturadas (fora de try/catch, em callbacks
 * e timers). Sem ele, em produção (Hermes/New Architecture) um throw não tratado
 * encerra o processo — aqui ao menos registramos a assinatura antes de delegar
 * ao handler anterior (que mantém o red box em dev). Idempotente.
 */
let installed = false;
export function installGlobalErrorHandler() {
  if (installed) return;
  installed = true;
  const g: any = globalThis as any;
  if (!g.ErrorUtils?.setGlobalHandler) return;
  const previous = g.ErrorUtils.getGlobalHandler?.();
  g.ErrorUtils.setGlobalHandler((error: any, isFatal?: boolean) => {
    void persistFatal(isFatal ? "fatal" : "non-fatal", error);
    // eslint-disable-next-line no-console
    console.error(
      "[GlobalError]",
      isFatal ? "FATAL" : "",
      error?.message ?? error,
      error?.stack,
    );
    if (typeof previous === "function") previous(error, isFatal);
  });
}

type Props = { children: React.ReactNode };
type State = { error: Error | null };

/**
 * Barreira de erro de render. Captura exceções na árvore React e mostra uma tela
 * de recuperação em vez de fechar o app. NÃO captura erros nativos (OOM/SIGSEGV)
 * nem rejeições assíncronas — para essas, o handler global acima registra a
 * assinatura. O fluxo de ponto é offline-first: pontos pendentes seguem salvos.
 */
export default class ErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null };

  constructor(props: Props) {
    super(props);
    installGlobalErrorHandler();
  }

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: any) {
    void persistFatal("render", error);
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary]", error?.message, info?.componentStack);
  }

  handleReset = () => this.setState({ error: null });

  render() {
    if (this.state.error) {
      return (
        <View style={styles.container}>
          <Ionicons name="warning-outline" size={56} color={PRIMARY} />
          <Text style={styles.title}>{T("Algo deu errado")}</Text>
          <Text style={styles.desc}>
            {T("Ocorreu um erro inesperado. Você pode tentar novamente — seus pontos pendentes continuam salvos e serão sincronizados.")}
          </Text>
          <Pressable style={styles.btn} onPress={this.handleReset}>
            <Text style={styles.btnText}>{T("Tentar novamente")}</Text>
          </Pressable>
        </View>
      );
    }
    return this.props.children as any;
  }
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    padding: 32,
    gap: 12,
    backgroundColor: "#fff",
  },
  title: {
    fontSize: 20,
    fontWeight: "800",
    color: PRIMARY,
    marginTop: 8,
  },
  desc: {
    fontSize: 14,
    color: "#526f7b",
    textAlign: "center",
    lineHeight: 20,
    maxWidth: 320,
  },
  btn: {
    marginTop: 16,
    backgroundColor: PRIMARY,
    paddingHorizontal: 28,
    paddingVertical: 14,
    borderRadius: 16,
  },
  btnText: {
    color: "#fff",
    fontWeight: "700",
    fontSize: 15,
  },
});
